import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import apiClient from '@skygroup/shared/API/apiClient';
import type { RootState } from '../../../store/store';

/**
 * Resolves whether the bot agent should render for the current tenant /
 * website. Source of truth is the backend composite check at
 * `GET /ms/bot-agent/availability` — it inspects:
 *   1. website.configuration.modulesConfigs.bot_agent.enabled
 *   2. tenant.llm_config validity
 *
 * Additionally honors a website-level toggle delivered through `/b2c/site/config`
 * (the legacy PHP path) when present, so admins can disable the widget purely
 * from the FE config without involving sky360-ms.
 *
 * When `available === false` no widget renders — the rest of the app behaves
 * exactly as it did before bot-agent existed.
 */
export interface WhatsappAvailability {
  enabled: boolean;
  business_number: string | null;
}

export function useBotAgentAvailability(): {
  available: boolean;
  loading: boolean;
  whatsapp: WhatsappAvailability | null;
} {
  const [available, setAvailable] = useState(false);
  const [whatsapp, setWhatsapp] = useState<WhatsappAvailability | null>(null);
  const [loading, setLoading] = useState(true);

  // Website-level toggle from /b2c/site/config (PHP). Optional — if the PHP
  // response doesn't include it yet, we still defer to the BE composite check.
  const siteFlag = useSelector(
    (s: RootState) =>
      (s.config.config as any)?.modulesConfigs?.bot_agent?.enabled,
  );

  useEffect(() => {
    let cancelled = false;

    // Short-circuit when the site config explicitly disables the bot. Saves
    // a network round-trip when admins have flipped it off.
    if (siteFlag === false) {
      setAvailable(false);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await apiClient.get('/ms/bot-agent/availability');

        if (!cancelled) {
          const data = res?.data?.data;
          setAvailable(data?.available === true);
          if (data?.whatsapp && typeof data.whatsapp === 'object') {
            setWhatsapp({
              enabled: data.whatsapp.enabled === true,
              business_number:
                typeof data.whatsapp.business_number === 'string'
                  ? data.whatsapp.business_number
                  : null,
            });
          } else {
            setWhatsapp(null);
          }
        }
      } catch {
        if (!cancelled) {
          setAvailable(false);
          setWhatsapp(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteFlag]);

  return { available, loading, whatsapp };
}
